import './style.scss'
import React, { useMemo, useState } from 'react'
import { Flex, Text, tokens } from 'src/componentv2/Base/ui'

export interface TableColumn<T = any> {
    /**
     * Unique key
     */
    key: string

    /**
     * Column title
     */
    title: React.ReactNode

    /**
     * Width (optional)
     */
    width?: string

    /**
     * Alignment
     */
    align?: 'left' | 'center' | 'right'

    /**
     * Field to display automatically
     */
    dataIndex?: keyof T

    /**
     * Custom renderer
     */
    render?: (row: T, index: number) => React.ReactNode
}

export interface InstitutionTableProps<T = any> {
    columns: TableColumn<T>[]
    data: T[]

    loading?: boolean

    emptyText?: React.ReactNode

    bordered?: boolean

    striped?: boolean

    hoverable?: boolean

    pageSize?: number

    currentPage?: number

    onPageChange?: (page: number) => void

    className?: string
}

export function InstitutionTable<T>({
                                        columns,
                                        data,
                                        loading = false,
                                        emptyText = 'No records found.',
                                        bordered = true,
                                        striped = false,
                                        hoverable = true,
                                        pageSize = 10,
                                        currentPage,
                                        onPageChange,
                                        className = '',
                                    }: InstitutionTableProps<T>) {

    const [internalPage, setInternalPage] = useState(1)

    const page = currentPage ?? internalPage

    const totalPages = Math.max(
        1,
        Math.ceil(data.length / pageSize),
    )

    const paginatedData = useMemo(() => {
        const start = (page - 1) * pageSize
        return data.slice(start, start + pageSize)
    }, [data, page, pageSize])

    const changePage = (next: number) => {
        if (next < 1 || next > totalPages) return

        if (onPageChange) {
            onPageChange(next)
        } else {
            setInternalPage(next)
        }
    }

    return (
        <div
            className={[
                'institution-table',
                bordered ? 'institution-table--bordered' : '',
                striped ? 'institution-table--striped' : '',
                hoverable ? 'institution-table--hoverable' : '',
                className,
            ].join(' ')}
        >

            <div className="institution-table__wrapper">

                <table className="institution-table__table">

                    <thead>

                    <tr>

                        {columns.map((column) => (

                            <th
                                key={column.key}
                                style={{
                                    width: column.width,
                                    textAlign: column.align ?? 'left',
                                }}
                            >
                                <Text
                                    fontSize="1.3"
                                    fontWeight="600"
                                    color={tokens.textMuted}
                                >
                                    {column.title}
                                </Text>
                            </th>

                        ))}

                    </tr>

                    </thead>

                    <tbody>

                    {loading && (

                        <tr>

                            <td
                                colSpan={columns.length}
                                className="institution-table__loading"
                            >

                                <Flex
                                    justify="center"
                                    paddingY="3"
                                >
                                    <Text>
                                        Loading...
                                    </Text>
                                </Flex>

                            </td>

                        </tr>

                    )}

                    {!loading &&
                        paginatedData.length === 0 && (

                            <tr>

                                <td
                                    colSpan={columns.length}
                                    className="institution-table__empty"
                                >

                                    <Flex
                                        direction="column"
                                        justify="center"
                                        items="center"
                                        paddingY="4"
                                    >

                                        <Text
                                            color={tokens.textMuted}
                                        >
                                            {emptyText}
                                        </Text>

                                    </Flex>

                                </td>

                            </tr>

                        )}

                    {!loading &&
                        paginatedData.length > 0 &&
                        paginatedData.map((row, index) => (                            <tr key={index}>

                                {columns.map((column) => {

                                    const value = column.dataIndex
                                        ? (row as any)[column.dataIndex]
                                        : undefined

                                    return (
                                        <td
                                            key={column.key}
                                            style={{
                                                textAlign:
                                                    column.align ?? 'left',
                                            }}
                                        >
                                            {column.render
                                                ? column.render(row, index)
                                                : (
                                                    <Text
                                                        fontSize="1.4"
                                                        color={tokens.textDark}
                                                    >
                                                        {value ?? '-'}
                                                    </Text>
                                                )}
                                        </td>
                                    )
                                })}

                            </tr>

                        ))}

                    </tbody>

                </table>

            </div>

            {totalPages > 1 && (

                <Flex
                    className="institution-table__pagination"
                    justify="space-between"
                    items="center"
                    topMargin="2"
                >

                    <Text
                        fontSize="1.3"
                        color={tokens.textMuted}
                    >
                        Showing {(page - 1) * pageSize + 1}
                        {' - '}
                        {Math.min(
                            page * pageSize,
                            data.length,
                        )}
                        {' of '}
                        {data.length}
                    </Text>

                    <Flex gap="0.8">

                        <button
                            type="button"
                            className="institution-table__page-btn"
                            disabled={page === 1}
                            onClick={() => changePage(page - 1)}
                        >
                            Previous
                        </button>

                        {Array.from({
                            length: totalPages,
                        }).map((_, i) => {

                            const pageNumber = i + 1

                            return (
                                <button
                                    key={pageNumber}
                                    type="button"
                                    className={`institution-table__page-btn ${
                                        page === pageNumber
                                            ? 'active'
                                            : ''
                                    }`}
                                    onClick={() =>
                                        changePage(pageNumber)
                                    }
                                >
                                    {pageNumber}
                                </button>
                            )
                        })}

                        <button
                            type="button"
                            className="institution-table__page-btn"
                            disabled={page === totalPages}
                            onClick={() => changePage(page + 1)}
                        >
                            Next
                        </button>

                    </Flex>

                </Flex>

            )}

        </div>
    )
}

export default InstitutionTable